import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";

type Platform = "twitter" | "bluesky" | "linkedin" | "newsletter" | "short_video";

const PLATFORM_LABEL: Record<string, string> = {
  twitter: "X / Twitter",
  bluesky: "Bluesky",
  linkedin: "LinkedIn",
  newsletter: "Newsletter",
  short_video: "Short video",
};

const DAY_LABEL = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

interface SocialAccountLite {
  id: string;
  platform: Platform;
  handle: string;
  display_name: string | null;
  status: string;
}

interface Category {
  id: string;
  name: string;
  color: string;
  position: number;
  scheduled_count: number;
  slot_count: number;
}

interface Slot {
  id: string;
  social_account_id: string;
  account_handle: string | null;
  platform: string | null;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  day_of_week: number;
  hour: number;
  minute: number;
  timezone: string;
}

interface ScheduledRow {
  id: string;
  platform: string;
  content: string;
  scheduled_for: number;
  status: string;
  social_account_id: string;
  category_id: string | null;
  added_via_queue: number;
}

function fmtHour(h: number, m: number): string {
  const hh = h.toString().padStart(2, "0");
  const mm = m.toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleString();
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

export default function QueuePage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [accounts, setAccounts] = useState<SocialAccountLite[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [posts, setPosts] = useState<ScheduledRow[]>([]);
  const [accountFilter, setAccountFilter] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const [showCatModal, setShowCatModal] = useState(false);
  const [showSlotModal, setShowSlotModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);

  async function loadAll() {
    setLoading(true);
    try {
      const acctParam = accountFilter
        ? `?social_account_id=${accountFilter}`
        : "";
      const [a, c, s, p] = await Promise.all([
        api.get<{ social_accounts: SocialAccountLite[] }>("/api/social-accounts"),
        api.get<{ categories: Category[] }>("/api/categories"),
        api.get<{ slots: Slot[] }>(`/api/schedule-slots${acctParam}`),
        api.get<{ scheduled_posts: ScheduledRow[] }>(
          "/api/scheduled-posts?status=pending&limit=500"
        ),
      ]);
      const active = (a.social_accounts ?? []).filter((x) => x.status === "active");
      setAccounts(active);
      setCategories(c.categories ?? []);
      setSlots(s.slots ?? []);
      setPosts(
        (p.scheduled_posts ?? []).filter((row) => row.added_via_queue === 1)
      );
      if (!accountFilter && active.length > 0 && active[0]) {
        setAccountFilter(active[0].id);
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountFilter]);

  useEffect(() => {
    if (slots.length === 0) return;
    const browserTz =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    if (browserTz === "UTC") return;
    const stale = slots.filter((s) => s.timezone === "UTC");
    if (stale.length === 0) return;
    void (async () => {
      try {
        await Promise.all(
          stale.map((s) =>
            api.patch(`/api/schedule-slots/${s.id}`, { timezone: browserTz })
          )
        );
        toast.success(
          `Migrated ${stale.length} slot${stale.length === 1 ? "" : "s"} to ${browserTz}.`
        );
        await loadAll();
      } catch (err) {
        toast.error(apiErrorMessage(err));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  const slotsByDayHour = useMemo(() => {
    const m = new Map<string, Slot[]>();
    for (const s of slots) {
      const key = `${s.day_of_week}:${s.hour}`;
      const arr = m.get(key) ?? [];
      arr.push(s);
      m.set(key, arr);
    }
    return m;
  }, [slots]);

  const upcomingByAccount = useMemo(() => {
    const m = new Map<string, ScheduledRow[]>();
    for (const p of posts) {
      const arr = m.get(p.social_account_id) ?? [];
      arr.push(p);
      m.set(p.social_account_id, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => a.scheduled_for - b.scheduled_for);
    }
    return m;
  }, [posts]);

  const filteredUpcoming = useMemo(() => {
    if (!accountFilter) return posts.slice().sort((a, b) => a.scheduled_for - b.scheduled_for);
    return upcomingByAccount.get(accountFilter) ?? [];
  }, [accountFilter, posts, upcomingByAccount]);

  const usedHours = useMemo(() => {
    const set = new Set<number>();
    for (const s of slots) set.add(s.hour);
    if (set.size === 0) [9, 12, 15, 18, 21].forEach((h) => set.add(h));
    return Array.from(set).sort((a, b) => a - b);
  }, [slots]);

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="page-title">Queue</h1>
          <p className="page-subtitle">
            Define recurring slots once. Add posts to the queue and they auto-fill the
            next free slot. Categories let you split slots by topic (e.g. Tips vs Threads).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-brand"
            disabled={accounts.length === 0}
          >
            + Add to queue
          </button>
          <button
            onClick={() => setShowCsvModal(true)}
            className="btn-secondary text-sm"
            disabled={accounts.length === 0}
          >
            ⇪ CSV import
          </button>
        </div>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3 text-sm">
        <label className="text-ink-600">Account:</label>
        <select
          className="input inline-block w-auto"
          value={accountFilter}
          onChange={(e) => setAccountFilter(e.target.value)}
        >
          <option value="">All accounts</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {PLATFORM_LABEL[a.platform] ?? a.platform} · @{a.handle}
            </option>
          ))}
        </select>
        <button
          onClick={() => setShowSlotModal(true)}
          className="btn-secondary text-sm"
          disabled={!accountFilter}
          title={accountFilter ? "" : "Pick an account first"}
        >
          + Slot
        </button>
        <button
          onClick={() => setShowCatModal(true)}
          className="btn-secondary text-sm"
        >
          Categories
        </button>
      </div>

      {accounts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ink-200 bg-white p-8 text-center text-sm text-ink-500">
          Connect a social account in Settings first.
        </div>
      ) : (
        <>
          <section className="mb-8">
            <h2 className="mb-3 text-base font-medium text-ink-900">Weekly slots</h2>
            <div className="overflow-x-auto rounded-xl border border-ink-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-ink-50 text-left text-ink-600">
                  <tr>
                    <th className="w-16 px-3 py-2 font-medium">Time</th>
                    {DAY_LABEL.map((d) => (
                      <th key={d} className="px-2 py-2 text-center font-medium">
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {usedHours.map((h) => (
                    <tr key={h} className="border-t border-ink-100">
                      <td className="px-3 py-2 font-mono text-xs text-ink-500">
                        {String(h).padStart(2, "0")}:00
                      </td>
                      {DAY_LABEL.map((_, dayIdx) => {
                        const ks = slotsByDayHour.get(`${dayIdx}:${h}`) ?? [];
                        return (
                          <td
                            key={dayIdx}
                            className="px-1 py-1 align-top text-center"
                          >
                            {ks.length === 0 ? (
                              <span className="text-ink-300">·</span>
                            ) : (
                              <div className="flex flex-col gap-1">
                                {ks.map((s) => (
                                  <SlotPill
                                    key={s.id}
                                    slot={s}
                                    onDelete={async () => {
                                      const ok = await confirm({
                                        title: "Delete slot",
                                        message: "Future posts using this slot will need to be re-queued.",
                                        confirmLabel: "Delete",
                                        destructive: true,
                                      });
                                      if (!ok) return;
                                      try {
                                        await api.del(`/api/schedule-slots/${s.id}`);
                                        await loadAll();
                                      } catch (err) {
                                        toast.error(apiErrorMessage(err));
                                      }
                                    }}
                                  />
                                ))}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {slots.length === 0 && accountFilter ? (
              <p className="mt-2 text-xs text-ink-500">
                No slots yet for this account. Click <b>+ Slot</b> to add one (e.g. Mon
                09:00). Posts in the queue will fill the next free occurrence.
              </p>
            ) : null}
          </section>

          <section className="mb-8">
            <h2 className="mb-3 text-base font-medium text-ink-900">
              Upcoming queue ({filteredUpcoming.length})
            </h2>
            {filteredUpcoming.length === 0 ? (
              <div className="rounded-lg border border-dashed border-ink-200 bg-white p-6 text-center text-sm text-ink-500">
                Queue is empty. Add a post or import a CSV.
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUpcoming.map((p) => {
                  const cat = categories.find((c) => c.id === p.category_id);
                  return (
                    <div
                      key={p.id}
                      className="flex items-center gap-3 rounded-xl border border-ink-200 bg-white p-3 shadow-sm"
                    >
                      {cat ? (
                        <span
                          className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                          style={{ backgroundColor: cat.color }}
                        >
                          {cat.name}
                        </span>
                      ) : (
                        <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600">
                          uncategorized
                        </span>
                      )}
                      <span className="text-xs font-medium text-ink-700">
                        {fmtDate(p.scheduled_for)}
                      </span>
                      <span className="flex-1 truncate text-sm text-ink-800">
                        {truncate(p.content, 120)}
                      </span>
                      <button
                        onClick={async () => {
                          const ok = await confirm({
                            title: "Remove from queue",
                            message: "This scheduled post will be removed from the queue.",
                            confirmLabel: "Remove",
                            destructive: true,
                          });
                          if (!ok) return;
                          try {
                            await api.del(`/api/scheduled-posts/${p.id}`);
                            await loadAll();
                          } catch (err) {
                            toast.error(apiErrorMessage(err));
                          }
                        }}
                        className="text-xs text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {loading ? <div className="text-sm text-ink-500">Loading…</div> : null}

      {showCatModal ? (
        <CategoriesModal
          categories={categories}
          onClose={() => setShowCatModal(false)}
          onChange={loadAll}
        />
      ) : null}
      {showSlotModal && accountFilter ? (
        <SlotModal
          accountId={accountFilter}
          categories={categories}
          onClose={() => setShowSlotModal(false)}
          onCreated={async () => {
            setShowSlotModal(false);
            await loadAll();
          }}
        />
      ) : null}
      {showAddModal ? (
        <AddToQueueModal
          accounts={accounts}
          categories={categories}
          defaultAccountId={accountFilter || (accounts[0]?.id ?? "")}
          onClose={() => setShowAddModal(false)}
          onAdded={async () => {
            setShowAddModal(false);
            await loadAll();
          }}
        />
      ) : null}
      {showCsvModal ? (
        <CsvImportModal
          accounts={accounts}
          categories={categories}
          defaultAccountId={accountFilter || (accounts[0]?.id ?? "")}
          onClose={() => setShowCsvModal(false)}
          onImported={async () => {
            setShowCsvModal(false);
            await loadAll();
          }}
        />
      ) : null}
    </div>
  );
}

function SlotPill({ slot, onDelete }: { slot: Slot; onDelete: () => void }) {
  const bg = slot.category_color ?? "#6366f1";
  return (
    <button
      onClick={onDelete}
      className="rounded-full px-2 py-0.5 text-[10px] font-medium text-white opacity-90 hover:opacity-100"
      style={{ backgroundColor: bg }}
      title={`${slot.category_name ?? "uncategorized"} · ${fmtHour(slot.hour, slot.minute)} · click to delete`}
    >
      {fmtHour(slot.hour, slot.minute)}
    </button>
  );
}

const COLOR_PALETTE = [
  "#6366f1", // indigo
  "#22c55e", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#06b6d4", // cyan
  "#a855f7", // purple
  "#ec4899", // pink
  "#10b981", // emerald
  "#0ea5e9", // sky
  "#f97316", // orange
];

function pickNextColor(used: string[]): string {
  const usedSet = new Set(used.map((c) => c.toLowerCase()));
  for (const c of COLOR_PALETTE) {
    if (!usedSet.has(c.toLowerCase())) return c;
  }
  const idx = used.length % COLOR_PALETTE.length;
  return COLOR_PALETTE[idx] ?? "#6366f1";
}

function CategoriesModal(props: {
  categories: Category[];
  onClose: () => void;
  onChange: () => void | Promise<void>;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [name, setName] = useState("");
  const [color, setColor] = useState(() =>
    pickNextColor(props.categories.map((c) => c.color))
  );
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setColor(pickNextColor(props.categories.map((c) => c.color)));
  }, [props.categories]);

  async function add() {
    if (!name.trim()) return;
    setCreating(true);
    try {
      await api.post("/api/categories", { name: name.trim(), color });
      setName("");
      await props.onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  }

  async function remove(id: string) {
    const ok = await confirm({
      title: "Delete category",
      message: "Posts and slots tagged with this category will become uncategorized.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/categories/${id}`);
      await props.onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function recolor(id: string, newColor: string) {
    try {
      await api.patch(`/api/categories/${id}`, { color: newColor });
      await props.onChange();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  return (
    <Modal title="Categories" onClose={props.onClose}>
      <div className="space-y-3 p-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              className="input flex-1"
              placeholder="Category name (e.g. Tips, Threads)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
            />
            <button
              onClick={add}
              disabled={creating || !name.trim()}
              className="btn-primary text-sm"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-ink-500">Color:</span>
            {COLOR_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                className={`h-6 w-6 rounded-full border-2 transition ${
                  color.toLowerCase() === c.toLowerCase()
                    ? "border-ink-900 scale-110"
                    : "border-white hover:border-ink-300"
                }`}
                style={{ backgroundColor: c }}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
        </div>
        {props.categories.length === 0 ? (
          <p className="text-sm text-ink-500">
            No categories yet. Create one to bucket your posts (e.g. Tips, Threads, Curation).
          </p>
        ) : (
          <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
            {props.categories.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                <input
                  type="color"
                  value={c.color}
                  onChange={(e) => recolor(c.id, e.target.value)}
                  className="h-5 w-5 cursor-pointer rounded-full border-0 bg-transparent p-0"
                  title="Click to change color"
                  style={{ backgroundColor: c.color }}
                />
                <span className="flex-1 text-sm font-medium text-ink-800">
                  {c.name}
                </span>
                <span className="text-xs text-ink-500">
                  {c.slot_count} slots · {c.scheduled_count} queued
                </span>
                <button
                  onClick={() => remove(c.id)}
                  className="text-xs text-red-600 hover:underline"
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}

function SlotModal(props: {
  accountId: string;
  categories: Category[];
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [day, setDay] = useState(1);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [categoryId, setCategoryId] = useState("");
  const [busy, setBusy] = useState(false);

  async function create() {
    setBusy(true);
    try {
      await api.post("/api/schedule-slots", {
        social_account_id: props.accountId,
        category_id: categoryId || null,
        day_of_week: day,
        hour,
        minute,
        timezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      });
      await props.onCreated();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="New recurring slot" onClose={props.onClose}>
      <div className="space-y-3 p-4 text-sm">
        <div>
          <label className="mb-1 block text-ink-600">Day of week</label>
          <select
            className="input w-full"
            value={day}
            onChange={(e) => setDay(parseInt(e.target.value, 10))}
          >
            {DAY_LABEL.map((d, i) => (
              <option key={i} value={i}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="mb-1 block text-ink-600">Hour</label>
            <select
              className="input w-full"
              value={hour}
              onChange={(e) => setHour(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="mb-1 block text-ink-600">Minute</label>
            <select
              className="input w-full"
              value={minute}
              onChange={(e) => setMinute(parseInt(e.target.value, 10))}
            >
              {[0, 15, 30, 45].map((m) => (
                <option key={m} value={m}>
                  {String(m).padStart(2, "0")}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-ink-600">Category (optional)</label>
          <select
            className="input w-full"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">— uncategorized —</option>
            {props.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="text-xs text-ink-500">
          Times are interpreted in UTC (your browser shows them in local time).
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={props.onClose} className="btn-secondary">
            Cancel
          </button>
          <button onClick={create} disabled={busy} className="btn-primary">
            {busy ? "Creating…" : "Create slot"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AddToQueueModal(props: {
  accounts: SocialAccountLite[];
  categories: Category[];
  defaultAccountId: string;
  onClose: () => void;
  onAdded: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [accountId, setAccountId] = useState(props.defaultAccountId);
  const [categoryId, setCategoryId] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!content.trim() || !accountId) return;
    setBusy(true);
    try {
      const res = await api.post<{ scheduled_for: number }>(
        "/api/scheduled-posts/queue",
        {
          social_account_id: accountId,
          content: content.trim(),
          category_id: categoryId || null,
        }
      );
      toast.success(`Queued for ${new Date(res.scheduled_for).toLocaleString()}`);
      await props.onAdded();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Add to queue" onClose={props.onClose}>
      <div className="space-y-3 p-4 text-sm">
        <div>
          <label className="mb-1 block text-ink-600">Account</label>
          <select
            className="input w-full"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            {props.accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {PLATFORM_LABEL[a.platform] ?? a.platform} · @{a.handle}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-ink-600">Category (optional)</label>
          <select
            className="input w-full"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">— uncategorized —</option>
            {props.categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-ink-600">Content</label>
          <textarea
            className="input min-h-[160px] w-full"
            placeholder="Write your post…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={props.onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={add}
            disabled={busy || !content.trim()}
            className="btn-primary"
          >
            {busy ? "Queuing…" : "Add to queue"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CsvImportModal(props: {
  accounts: SocialAccountLite[];
  categories: Category[];
  defaultAccountId: string;
  onClose: () => void;
  onImported: () => void | Promise<void>;
}) {
  const toast = useToast();
  const [accountId, setAccountId] = useState(props.defaultAccountId);
  const [categoryId, setCategoryId] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => {
    return text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0)
      .slice(0, 200);
  }, [text]);

  async function importNow() {
    if (rows.length === 0 || !accountId) return;
    setBusy(true);
    try {
      const res = await api.post<{
        created: { id: string; scheduled_for: number }[];
        skipped: { index: number; reason: string }[];
      }>("/api/scheduled-posts/bulk-csv", {
        social_account_id: accountId,
        default_category_id: categoryId || null,
        rows: rows.map((content) => ({ content })),
      });
      toast.success(
        `Imported ${res.created.length}/${rows.length} (skipped ${res.skipped.length}).`
      );
      await props.onImported();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Bulk import (one post per line)" onClose={props.onClose}>
      <div className="space-y-3 p-4 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-ink-600">Account</label>
            <select
              className="input w-full"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              {props.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {PLATFORM_LABEL[a.platform] ?? a.platform} · @{a.handle}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-ink-600">Default category</label>
            <select
              className="input w-full"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">— uncategorized —</option>
              {props.categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-ink-600">
            Posts ({rows.length} parsed, max 200)
          </label>
          <textarea
            className="input min-h-[260px] w-full font-mono text-xs"
            placeholder={
              "Paste posts here, one per line.\n\nExample:\nThe best engineers are great teachers.\n3 lessons from 18 months of running async-first teams: ...\nA hard truth about remote work…"
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        <div className="text-xs text-ink-500">
          Each line becomes one queued post. Posts auto-fill the next free slot of the
          selected account / category. If you don't have enough slots, extras are
          skipped.
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={props.onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={importNow}
            disabled={busy || rows.length === 0}
            className="btn-primary"
          >
            {busy ? "Importing…" : `Import ${rows.length} post${rows.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal(props: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in"
      onClick={props.onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-ink-200/60 bg-white shadow-xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
          <h3 className="text-base font-semibold text-ink-900">{props.title}</h3>
          <button
            onClick={props.onClose}
            className="text-ink-500 hover:text-ink-900"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {props.children}
      </div>
    </div>
  );
}
